// ═════════════════════════════════════════════════════════════════════════════
// Service Master admin API client
//
// Wraps the EMR service-master endpoints (see ipd-service-master-flow.md at the
// repo root). Tenant-wide catalog — no admission id. All calls are EMR wrappers
// under /app/api/ipd/billing/service-master/*. The UI never talks to billing
// microservices directly — EMR headers only. Same conventions as
// corporate-api.js / package-billing-api.js (own request()/unwrapObject()/
// unwrapList()/qs() helpers rather than a shared base module).
//
//   Lookups         GET  /lookups/?active_only=
//   Groups          GET/POST  /groups/[{id}/]         (edit + deactivate: POST/DELETE)
//   Classifications GET/POST  /classifications/[{id}/]
//                   POST      /group-by-classification/  { classification_code }
//   Sample types    GET/POST  /sample-types/[{id}/]
//   Service types   GET/POST  /service-types/[{id}/]   (the parts of the bill)
//   Services        GET/POST  /services/[{id}/]
//                   POST .../{id}/flags/   POST .../{id}/rate/
//   Import          GET  /services/import-template/
//                   POST /services/import/   (multipart)
// ═════════════════════════════════════════════════════════════════════════════

import axios from "axios";
import { getBillingHeaders, trimId, sanitizeErrorMessage } from "./billing-api";

const API_ROOT = process.env.REACT_APP_BACKEND_ROUTE_URL || "";
const SM_ROOT = `${API_ROOT}ipd/billing/service-master`;

// ── low-level helpers ─────────────────────────────────────────────────────────

function num(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

function unwrapObject(data) {
  if (!data || typeof data !== "object") return data;
  if (data.data && typeof data.data === "object" && !Array.isArray(data.data)) {
    return data.data;
  }
  return data;
}

function unwrapList(data, keys = []) {
  if (Array.isArray(data)) return data;
  for (const key of ["items", "data", "results", ...keys]) {
    const val = data?.[key];
    if (Array.isArray(val)) return val;
    if (val && typeof val === "object" && Array.isArray(val.items)) return val.items;
  }
  return [];
}

/** JSON request with the shared billing error handling (same shape as corporate-api.js). */
async function request(method, url, body) {
  try {
    const res = await axios({
      method,
      url,
      data: body,
      headers: getBillingHeaders(body != null),
    });
    if (res.data?.status === "Failed") {
      const wrapped = new Error(sanitizeErrorMessage(res.data.message || "Request failed"));
      wrapped.status = res.data.status_code || res.data.code;
      // A deactivation the server refused because rows still point at the master.
      // Carried through so the caller can show what is attached and offer to go
      // ahead, instead of only printing the message.
      wrapped.requiresForce = res.data.requires_force === true;
      wrapped.serviceCount = res.data.service_count ?? null;
      wrapped.classificationCount = res.data.classification_count ?? null;
      // Already sanitised and already carrying its fields - the catch below has to
      // rethrow it as it is rather than build a fresh Error from the message.
      wrapped.handled = true;
      throw wrapped;
    }
    return res.data;
  } catch (err) {
    if (err.response?.status === 401) window.location.href = "/login";
    if (err?.handled) throw err;
    if (err && err.status !== undefined && !err.response) throw err;
    const status = err.response?.status;
    const rawMsg =
      err.response?.data?.message ||
      err.response?.data?.detail ||
      (typeof err.response?.data === "string" ? err.response.data : "") ||
      err.message ||
      "Request failed";
    const wrapped = new Error(sanitizeErrorMessage(rawMsg, status));
    wrapped.status = status;
    wrapped.code = err.response?.data?.code || err.response?.data?.error_code;
    throw wrapped;
  }
}

/**
 * Multipart upload — must NOT force application/json so axios can set the
 * multipart boundary itself. getBillingHeaders(false) omits Content-Type.
 */
async function uploadRequest(url, formData) {
  try {
    const res = await axios.post(url, formData, { headers: getBillingHeaders(false) });
    if (res.data?.status === "Failed") {
      const wrapped = new Error(sanitizeErrorMessage(res.data.message || "Upload failed"));
      wrapped.status = res.data.status_code || res.data.code;
      throw wrapped;
    }
    return res.data;
  } catch (err) {
    if (err.response?.status === 401) window.location.href = "/login";
    if (err && err.status !== undefined && !err.response) throw err;
    const status = err.response?.status;
    const rawMsg =
      err.response?.data?.message || err.response?.data?.detail || err.message || "Upload failed";
    const wrapped = new Error(sanitizeErrorMessage(rawMsg, status));
    wrapped.status = status;
    throw wrapped;
  }
}

function qs(params) {
  const sp = new URLSearchParams();
  Object.entries(params || {}).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== "") sp.set(k, String(v));
  });
  const s = sp.toString();
  return s ? `?${s}` : "";
}

// ── mappers (API → UI) ────────────────────────────────────────────────────────

export function mapGroup(g = {}) {
  return {
    id: g.id ?? g.group_id,
    code: g.group_code ?? "—",
    name: g.name ?? g.group_code ?? "—",
    description: g.description ?? "",
    serviceCount: g.service_count ?? null,
    classificationCount: g.classification_count ?? null,
    active: g.is_active ?? true,
  };
}

export function mapClassification(c = {}) {
  return {
    id: c.id ?? c.classification_id,
    groupId: c.service_group_id ?? c.group_id ?? null,
    groupCode: c.group_code ?? null,
    groupName: c.group_name ?? null,
    code: c.classification_code ?? "—",
    name: c.name ?? c.classification_code ?? "—",
    description: c.description ?? "",
    serviceCount: c.service_count ?? null,
    active: c.is_active ?? true,
  };
}

export function mapSampleType(s = {}) {
  return {
    id: s.id ?? s.sample_id,
    code: s.sample_code ?? "—",
    name: s.name ?? s.sample_code ?? "—",
    serviceCount: s.service_count ?? null,
    active: s.is_active ?? true,
  };
}

/**
 * A service type — the part of the bill a service prints under (Bed Charges, OT,
 * Investigations, …). The hospital maintains the list; it is not a fixed set.
 */
export function mapServiceType(t = {}) {
  return {
    id: t.id,
    name: t.name ?? t.service_name ?? "—",
    serviceCount: t.service_count ?? null,
    active: t.is_active ?? true,
  };
}

export function mapBedType(b = {}) {
  return {
    id: b.id,
    code: b.code ?? b.bed_type_code ?? "—",
    name: b.name ?? b.code ?? "—",
    dailyCharge: b.daily_charge != null ? num(b.daily_charge) : null,
  };
}

export function mapService(s = {}) {
  return {
    id: s.id ?? s.medical_service_id,
    name: s.name ?? "—",
    uniqueCode: s.unique_code ?? null,
    serviceCode: s.service_code ?? null,
    cptCode: s.cpt_code ?? s.cptCode ?? null,
    effectiveFrom: s.effective_from ?? null,
    effectiveTo: s.effective_to ?? null,
    groupId: s.service_group_id ?? s.group_id ?? null,
    groupName: s.service_group_name ?? s.group_name ?? null,
    classificationId: s.service_classification_id ?? s.classification_id ?? null,
    classificationName: s.service_classification_name ?? s.classification_name ?? null,
    bedTypeCode: s.bed_type_code ?? null,
    sampleId: s.sample_id ?? null,
    sampleName: s.sample_name ?? null,
    serviceTypeId: s.service_type_id ?? null,
    serviceTypeName: s.service_type_name ?? null,
    // The older free-text tag on the service row — kept for existing data, not the
    // same thing as serviceTypeId above.
    serviceKind: s.service_type ?? null,
    unit: s.unit ?? null,
    fee: s.fee != null ? num(s.fee) : null,
    chargeModifiable: s.charge_modifiable === true,
    quantityModifiable: s.quantity_modifiable === true,
    description: s.description ?? "",
    active: s.active ?? s.is_active ?? true,
  };
}

// ── Lookups (bootstrap — one call fills every filter dropdown) ───────────────

export async function listLookups({ activeOnly = true } = {}) {
  const data = await request("GET", `${SM_ROOT}/lookups/${qs({ active_only: activeOnly })}`);
  const obj = unwrapObject(data);
  return {
    groups: unwrapList(obj, ["groups"]).map(mapGroup),
    classifications: unwrapList(obj, ["classifications"]).map(mapClassification),
    sampleTypes: unwrapList(obj, ["sample_types"]).map(mapSampleType),
    bedTypes: unwrapList(obj, ["bed_types"]).map(mapBedType),
    serviceTypes: unwrapList(obj, ["service_types"]).map(mapServiceType),
    // False on a hospital that has not been given the service type list yet — the
    // screen hides the picker rather than offering an empty dropdown.
    serviceTypesSupported: obj.service_types_supported !== false,
  };
}

// ── Service types (the parts of the bill) ─────────────────────────────────────

export async function listServiceTypes({ activeOnly = true } = {}) {
  const data = await request("GET", `${SM_ROOT}/service-types/${qs({ active_only: activeOnly })}`);
  return unwrapList(data, ["service_types"]).map(mapServiceType);
}

export async function createServiceType(body) {
  const data = await request("POST", `${SM_ROOT}/service-types/`, body);
  const obj = unwrapObject(data);
  return mapServiceType(obj.service_type || obj);
}

export async function updateServiceType(serviceTypeId, body) {
  const data = await request("POST", `${SM_ROOT}/service-types/${trimId(serviceTypeId)}/`, body);
  const obj = unwrapObject(data);
  return mapServiceType(obj.service_type || obj);
}

/** Soft-deactivate. Refused with `requiresForce` while services are still filed under it. */
export async function deleteServiceType(serviceTypeId, { force = false } = {}) {
  const data = await request(
    "POST",
    `${SM_ROOT}/service-types/${trimId(serviceTypeId)}/delete/`,
    force ? { force: true } : undefined
  );
  return unwrapObject(data);
}

// ── Groups ─────────────────────────────────────────────────────────────────────

export async function listGroups({ search, activeOnly = true } = {}) {
  const data = await request("GET", `${SM_ROOT}/groups/${qs({ search, active_only: activeOnly })}`);
  return unwrapList(data, ["groups"]).map(mapGroup);
}

export async function createGroup(body) {
  const data = await request("POST", `${SM_ROOT}/groups/`, body);
  const obj = unwrapObject(data);
  return mapGroup(obj.group || obj);
}

/** Rename / description update — POST, not PATCH (per spec). */
export async function updateGroup(groupId, body) {
  const data = await request("POST", `${SM_ROOT}/groups/${trimId(groupId)}/`, body);
  const obj = unwrapObject(data);
  return mapGroup(obj.group || obj);
}

/**
 * Soft-deactivate (is_active=false) via POST (platform is GET/POST only).
 * Refused with `requiresForce` while services or classifications are still filed
 * under the group; call again with { force: true } once the user has confirmed.
 */
export async function deleteGroup(groupId, { force = false } = {}) {
  const data = await request(
    "POST",
    `${SM_ROOT}/groups/${trimId(groupId)}/delete/`,
    force ? { force: true } : undefined
  );
  return unwrapObject(data);
}

// ── Classifications ───────────────────────────────────────────────────────────

export async function listClassifications({ groupId, search, activeOnly = true } = {}) {
  const data = await request(
    "GET",
    `${SM_ROOT}/classifications/${qs({ group_id: groupId, search, active_only: activeOnly })}`
  );
  return unwrapList(data, ["classifications"]).map(mapClassification);
}

export async function createClassification(body) {
  const data = await request("POST", `${SM_ROOT}/classifications/`, body);
  const obj = unwrapObject(data);
  return mapClassification(obj.classification || obj);
}

/**
 * Rename, re-code, or move the classification to a different group. Moving it
 * re-files every service under it into the new group too (a service carries both
 * ids), and `servicesRefiled` says how many moved.
 */
export async function updateClassification(classificationId, body) {
  const data = await request("POST", `${SM_ROOT}/classifications/${trimId(classificationId)}/`, body);
  const obj = unwrapObject(data);
  return {
    ...mapClassification(obj.classification || obj),
    servicesRefiled: obj.services_refiled ?? 0,
  };
}

/** Soft-deactivate. Refused with `requiresForce` while services are still filed under it. */
export async function deleteClassification(classificationId, { force = false } = {}) {
  const data = await request(
    "POST",
    `${SM_ROOT}/classifications/${trimId(classificationId)}/delete/`,
    force ? { force: true } : undefined
  );
  return unwrapObject(data);
}

/**
 * POST .../group-by-classification/  { classification_code }
 * Resolves one classification (and its parent service group) by code — e.g.
 * "LAB-CBC" → Hematology under Laboratory. Does not return services; pair with
 * listServices({ classificationId }) or charge-capture search afterward.
 */
export async function groupByClassification({ classificationCode }) {
  const code = String(classificationCode || "").trim();
  if (!code) throw new Error("classification_code is required");
  const data = await request("POST", `${SM_ROOT}/group-by-classification/`, {
    classification_code: code,
  });
  const obj = unwrapObject(data);
  const c = obj.classification;
  return {
    classification: c
      ? {
          id: c.id ?? c.classification_id,
          serviceGroupId: c.service_group_id ?? c.group_id ?? null,
          groupCode: c.group_code ?? null,
          groupName: c.group_name ?? null,
          code: c.classification_code ?? code,
          name: c.name ?? c.classification_code ?? "—",
          description: c.description ?? "",
          active: c.is_active ?? true,
        }
      : null,
    serviceGroup: obj.service_group ? mapGroup(obj.service_group) : null,
  };
}

// ── Sample types ───────────────────────────────────────────────────────────────

export async function listSampleTypes({ search, activeOnly = true } = {}) {
  const data = await request(
    "GET",
    `${SM_ROOT}/sample-types/${qs({ search, active_only: activeOnly })}`
  );
  return unwrapList(data, ["sample_types"]).map(mapSampleType);
}

export async function createSampleType(body) {
  const data = await request("POST", `${SM_ROOT}/sample-types/`, body);
  const obj = unwrapObject(data);
  return mapSampleType(obj.sample_type || obj);
}

export async function updateSampleType(sampleId, body) {
  const data = await request("POST", `${SM_ROOT}/sample-types/${trimId(sampleId)}/`, body);
  const obj = unwrapObject(data);
  return mapSampleType(obj.sample_type || obj);
}

/** Soft-deactivate. Refused with `requiresForce` while it is the sample on live services. */
export async function deleteSampleType(sampleId, { force = false } = {}) {
  const data = await request(
    "POST",
    `${SM_ROOT}/sample-types/${trimId(sampleId)}/delete/`,
    force ? { force: true } : undefined
  );
  return unwrapObject(data);
}

// ── Services — heart of the screen ────────────────────────────────────────────

/**
 * Main filterable grid.
 * GET .../services/?group_id=&classification_id=&bed_type_code=&sample_id=&
 *   service_type=&unique_code=&search=&charge_modifiable=&quantity_modifiable=&
 *   active_only=&limit=&offset=
 */
export async function listServices({
  groupId,
  classificationId,
  bedTypeCode,
  sampleId,
  serviceType,
  serviceTypeId,
  uniqueCode,
  search,
  chargeModifiable,
  quantityModifiable,
  activeOnly = true,
  limit = 100,
  offset = 0,
} = {}) {
  const data = await request(
    "GET",
    `${SM_ROOT}/services/${qs({
      group_id: groupId,
      classification_id: classificationId,
      bed_type_code: bedTypeCode,
      sample_id: sampleId,
      service_type: serviceType,
      service_type_id: serviceTypeId,
      unique_code: uniqueCode,
      search,
      charge_modifiable: chargeModifiable,
      quantity_modifiable: quantityModifiable,
      active_only: activeOnly,
      limit,
      offset,
    })}`
  );
  const obj = unwrapObject(data);
  return {
    items: unwrapList(obj, ["services"]).map(mapService),
    count: obj.count ?? obj.total ?? (Array.isArray(obj.services) ? obj.services.length : 0),
  };
}

export async function getService(serviceId) {
  const data = await request("GET", `${SM_ROOT}/services/${trimId(serviceId)}/`);
  const obj = unwrapObject(data);
  return mapService(obj.service || obj);
}

export async function createService(body) {
  const data = await request("POST", `${SM_ROOT}/services/`, body);
  const obj = unwrapObject(data);
  return mapService(obj.service || obj);
}

/** Edit metadata / move group / unique_code — POST, not PATCH (per spec). */
export async function updateService(serviceId, body) {
  const data = await request("POST", `${SM_ROOT}/services/${trimId(serviceId)}/`, body);
  const obj = unwrapObject(data);
  return mapService(obj.service || obj);
}

/** Soft-deactivate (active=false) via POST (platform is GET/POST only). */
export async function deleteService(serviceId) {
  const data = await request("POST", `${SM_ROOT}/services/${trimId(serviceId)}/delete/`);
  return unwrapObject(data);
}

/** POST .../services/{id}/flags/  { charge_modifiable, quantity_modifiable } */
export async function setServiceFlags(serviceId, { chargeModifiable, quantityModifiable }) {
  const data = await request("POST", `${SM_ROOT}/services/${trimId(serviceId)}/flags/`, {
    charge_modifiable: chargeModifiable,
    quantity_modifiable: quantityModifiable,
  });
  return unwrapObject(data);
}

/**
 * POST .../services/{id}/rate/  { fee, force_rate_edit? }
 * `forceRateEdit` is required when the service's charge_modifiable is false —
 * UI should hide it behind a confirmed "Admin override" action.
 */
export async function setServiceRate(serviceId, { fee, forceRateEdit = false } = {}) {
  const body = { fee: String(fee) };
  if (forceRateEdit) body.force_rate_edit = true;
  const data = await request("POST", `${SM_ROOT}/services/${trimId(serviceId)}/rate/`, body);
  return unwrapObject(data);
}

// ── CSV import ─────────────────────────────────────────────────────────────────

/** GET import-template/ → CSV text for the download button. */
export async function getImportTemplate() {
  const data = await request("GET", `${SM_ROOT}/services/import-template/`);
  const obj = unwrapObject(data);
  return obj.csv ?? obj.template ?? (typeof obj === "string" ? obj : "");
}

/**
 * CSV/Excel upload (multipart). Returns the import summary
 * { total_rows, created_count, error_count, created:[], errors:[] } — some
 * responses nest this under `import`, so callers should read defensively.
 */
export async function importServices(file, { dryRun = true, createMissingMasters = true } = {}) {
  const fd = new FormData();
  fd.append("file", file);
  fd.append("dry_run", dryRun ? "true" : "false");
  fd.append("create_missing_masters", createMissingMasters ? "true" : "false");
  const data = await uploadRequest(`${SM_ROOT}/services/import/`, fd);
  const obj = unwrapObject(data);
  return obj.import || obj;
}
